import { Employee } from '../../types';
import { arabicEmployeeName } from '../../utils/employeeName';
import { LetterLayout, buildLetterHeaderHtml, buildLetterFooterHtml } from '../../components/HR/PersonnelLetterTemplate';

export interface RaaftFormState {
  passportOffice: string;
  vesselId: string;
  flag: string;
  ownerNationality: string;
  fromPort: string;
  currentLocation: string;
  selectedEmployeeIds: string[];
  /** The sign-off batch date this letter covers (yyyy-mm-dd). */
  batchDate?: string;
}

/** A crew-change batch: employees whose latest sign-off shares one date. */
export interface CrewChangeBatch {
  date: string;
  label: string;
  employeeIds: string[];
  /** Vessel (branch) the batch belongs to — set into the form on select. */
  vesselId?: string;
}

export interface RaaftLetterOptions {
  layout?: LetterLayout;
  /** Sign-off date taken from the latest matching crew change ({{CHANGE_DATE}}). */
  changeDate?: string;
}

/** dd/mm/yyyy in Latin digits, wrapped as an LTR token so the RTL layout
 *  can neither reorder it nor push it outside the printable area. */
const formatBirthDate = (value?: string): string =>
  value ? `<span dir="ltr">${new Date(value).toLocaleDateString('en-GB')}</span>` : '';

/** Default editable letter body. Tokens are replaced at generation time. */
export const RAAFT_DEFAULT_BODY = `<div class="recipient">السيد / مدير قسم جوازات {{PASSPORT_OFFICE}}</div>
<div class="body-text"><strong>تحية طيبة وبعد ،،،</strong></div>
<div class="body-text">الرجاء من سيادتكم الموافقة علي رفت عدد {{COUNT}} بحرى من على الوحدة {{VESSEL_NAME}} التابعة لشركة بترومارين لدعم المشاريع البحرية والمسجلة تحت علم {{FLAG}} وجنسية المالك {{OWNER_NATIONALITY}} القادمة من ميناء {{FROM_PORT}} والمتواجدة حاليا {{CURRENT_LOCATION}}</div>
{{EMPLOYEE_TABLE}}
<div class="footer-text">ولسيادتكم جزيل الشكر</div>
<div class="signature">مقدمة لسيادتكم توكيل سيلفرسي</div>`;

export function buildLetterHtml(
  form: RaaftFormState,
  vesselName: string,
  selectedEmployees: Employee[],
  templateContent?: string,
  options?: RaaftLetterOptions
): string {
  // Column order is written for dir="rtl": the first <td> renders
  // rightmost, so م (serial) leads on the right like official letters.
  const rows = selectedEmployees
    .map(
      (emp, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${arabicEmployeeName(emp)}</td>
          <td>${emp.position || ''}</td>
          <td>${emp.seamanBookNumber || ''}</td>
          <td>${formatBirthDate(emp.birthDate)}</td>
          <td>${emp.address || ''}</td>
        </tr>`
    )
    .join('');

  const employeeTable = `<table class="emp-table">
    <colgroup>
      <col style="width:5%">
      <col style="width:24%">
      <col style="width:12%">
      <col style="width:15%">
      <col style="width:14%">
      <col style="width:30%">
    </colgroup>
    <thead>
      <tr>
        <th>م</th>
        <th>الاسم</th>
        <th>الوظيفة</th>
        <th>رقم الجواز البحري</th>
        <th>تاريخ الميلاد</th>
        <th>العنوان</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="6">لا يوجد بحارة مختارين</td></tr>'}
    </tbody>
  </table>`;

  const employeeNames = selectedEmployees
    .map((emp, i) => `<div>${i + 1}. ${arabicEmployeeName(emp)}</div>`)
    .join('');

  const today = new Date().toLocaleDateString('ar-EG');
  const changeDate = options?.changeDate
    ? new Date(options.changeDate).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
    : today;

  // Use the admin-edited template body when it carries tokens; otherwise
  // fall back to the built-in default wording.
  const rawBody = templateContent && templateContent.includes('{{') ? templateContent : RAAFT_DEFAULT_BODY;
  let body = rawBody
    .replace(/{{DATE}}/g, today)
    .replace(/{{CHANGE_DATE}}/g, changeDate)
    .replace(/{{COMPANY_NAME}}/g, 'شركة بترومارين لدعم المشاريع البحرية')
    .replace(/{{PASSPORT_OFFICE}}/g, form.passportOffice || '')
    .replace(/{{VESSEL_NAME}}/g, vesselName)
    .replace(/{{FLAG}}/g, form.flag || '')
    .replace(/{{OWNER_NATIONALITY}}/g, form.ownerNationality || '')
    .replace(/{{FROM_PORT}}/g, form.fromPort || '')
    .replace(/{{CURRENT_LOCATION}}/g, form.currentLocation || '')
    .replace(/{{COUNT}}/g, String(selectedEmployees.length))
    .replace(/{{EMPLOYEE_NAMES}}/g, employeeNames);
  body = body.includes('{{EMPLOYEE_TABLE}}')
    ? body.replace(/{{EMPLOYEE_TABLE}}/g, employeeTable)
    : body + employeeTable;

  const hasFooter = !!options?.layout?.footerHtml?.trim();

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>طلب رفت بحارة</title>
  <style>
    @page { margin: 20mm; size: A4; }
    body {
      font-family: 'Arial', 'Tahoma', sans-serif;
      direction: rtl;
      text-align: right;
      font-size: 14px;
      line-height: 1.8;
      margin: 0;
      padding: 30px;
      ${hasFooter ? 'padding-bottom: 90px;' : ''}
    }
    .recipient { margin-bottom: 18px; font-size: 15px; font-weight: bold; }
    .body-text { margin-bottom: 18px; font-size: 14px; line-height: 2; }
    .emp-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      table-layout: fixed;
    }
    .emp-table th, .emp-table td {
      border: 1px solid #000;
      padding: 6px 5px;
      text-align: center;
      vertical-align: middle;
      font-size: 12px;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    .emp-table th {
      background: #e0e0e0;
      font-weight: bold;
    }
    .footer-text {
      margin-top: 30px;
      font-size: 14px;
      line-height: 2;
    }
    .signature { margin-top: 40px; font-weight: bold; }
    @media print { body { margin: 0; padding: 20px; ${hasFooter ? 'padding-bottom: 90px;' : ''} } }
  </style>
</head>
<body>
  ${buildLetterHeaderHtml(options?.layout, '')}
  ${body}
  ${buildLetterFooterHtml(options?.layout, true)}
</body>
</html>`;
}

export function buildTemplatePrintHtml(subject: string, content: string): string {
  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>${subject}</title>
  <style>
    @page { margin: 20mm; size: A4; }
    body { font-family: 'Arial','Tahoma',sans-serif; direction: rtl; text-align: right; font-size: 14px; line-height: 1.8; padding: 30px; }
    .content { white-space: pre-line; margin: 20px 0; }
    @media print { body { margin: 0; padding: 20px; } }
  </style>
</head>
<body>
  <div class="content">${content}</div>
</body>
</html>`;
}
